/**
 * @module NotificationPreferencesPage
 * @package @geenius/notifications-solidjs-css
 * @description Renders the full-page notification preference settings screen
 * for the SolidJS vanilla CSS variant.
 */

import { FadeIn } from '@geenius/motion/solidjs-css'
import type {
  NotificationChannel,
  NotificationPreferences,
  NotificationType,
} from '@geenius/notifications/shared'
import {
  buildNotificationsSeoMeta,
  DEFAULT_CHANNEL_PREFS,
  DEFAULT_TYPE_PREFS,
} from '@geenius/notifications/shared'
import { SEOHead } from '@geenius/seo/solidjs-css'
import type { JSX } from 'solid-js'
import { For, Show } from 'solid-js'
import { NotificationPreferencesForm } from '../components/NotificationPreferencesForm'
import { cx } from '../lib/cx'
import { createNotificationCopy } from '../lib/notificationCopy'
import { createNotificationPreferences } from '../primitives/createNotificationPreferences'

/**
 * Props for the SolidJS CSS notification preferences page.
 */
export interface NotificationPreferencesPageProps {
  preferences: NotificationPreferences | null | undefined
  onUpdate?: (
    channels?: Record<NotificationChannel, boolean>,
    types?: Record<NotificationType, boolean>,
    quietHours?: { start: string; end: string },
  ) => Promise<void>
  class?: string
}

const CHANNELS: NotificationChannel[] = ['in-app', 'email', 'push', 'sms']
const TYPES: NotificationType[] = [
  'info',
  'success',
  'warning',
  'error',
  'mention',
  'invite',
  'payment',
  'system',
]

/**
 * Renders the full-page notification preferences experience.
 *
 * @param props Page props containing current preferences and update callback.
 * @returns The preferences page markup.
 */
export function NotificationPreferencesPage(
  props: NotificationPreferencesPageProps,
): JSX.Element {
  const { t } = createNotificationCopy()
  const state = createNotificationPreferences(() => props.preferences, props.onUpdate)

  const resolvedPreferences = () =>
    state.preferences() ?? {
      userId: '',
      channels: { ...DEFAULT_CHANNEL_PREFS },
      types: { ...DEFAULT_TYPE_PREFS },
      quietHours: { start: '22:00', end: '07:00' },
    }

  const seoMeta = buildNotificationsSeoMeta({
    title: t('notifications.seo.preferencesTitle'),
    description: t('notifications.seo.preferencesDescription'),
    path: '/notifications/preferences',
    keywords: ['notifications', 'preferences', 'channels', 'quiet hours'],
  })

  return (
    <>
      <SEOHead meta={seoMeta} />
      <Show
        when={!state.isLoading()}
        fallback={
          <div class={cx('notifications__page', 'notifications__page-prefs', props.class)}>
            <For each={Array.from({ length: 8 })}>
              {() => (
                <div
                  class={cx(
                    'notifications__skeleton',
                    'notifications__skeleton--pref',
                  )}
                />
              )}
            </For>
          </div>
        }
      >
        <div class={cx('notifications__page', 'notifications__page-prefs', props.class)}>
          <FadeIn>
            
              <h1 class={cx('notifications__page-title')}>
                {t('notifications.preferences.pageTitle')}
              </h1>
              <p class={cx('notifications__page-subtitle')} style={{ 'margin-bottom': '2rem' }}>
                {t('notifications.preferences.pageDescription')}
              </p>
            
          </FadeIn>
          <FadeIn delay={80}>
            <NotificationPreferencesForm
              preferences={resolvedPreferences()}
              onUpdate={async (channels, types, quietHours) => {
                if (channels) {
                  for (const channel of CHANNELS) {
                    if (resolvedPreferences().channels[channel] !== channels[channel]) {
                      await state.toggleChannel(channel, channels[channel])
                    }
                  }
                }

                if (types) {
                  for (const type of TYPES) {
                    if (resolvedPreferences().types[type] !== types[type]) {
                      await state.toggleType(type, types[type])
                    }
                  }
                }

                if (quietHours) {
                  await state.setQuietHours(quietHours.start, quietHours.end)
                }
              }}
            />
          </FadeIn>
        </div>
      </Show>
    </>
  )
}
